/**
 * MobileFileSheet — the touch "file detail" surface reached from every row's
 * ⋯ button (MobileDriveScreen). OneDrive-style: a type header, a row of
 * horizontal quick-actions, then the rest as a borderless list. The grabber is
 * REAL — swipe the sheet down to dismiss. Slides up on open, down on close.
 *
 * Borderless by design (§DESIGN no-border rule): no dividers, no header rule —
 * grouping is spacing + the quick-action tiles. Portals to document.body
 * (outside any ThemedArea), so it re-applies the theme class + ws-clear.
 *
 * Dialog / confirm / picker flows (share, access, rename, copy, move, delete)
 * are raised to the parent via callbacks — the sheet stays presentational.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Lock, Link2, LayoutGrid,
} from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { FileIcon, fileIconProps, MiniappIcon, useTheme, getThemeClass } from '@papan/core-ui/ui'
import { type FileItem, buildFileActions, canManageAccessFor, type FileActionItem, type FileActionHandlers, type FileActionId, MiniappIconEditorDialog } from '@papan/files/ui'
import { presentsAsMiniapp, isProjectFolder, stripProjectFolderExt, stripMiniappFolderExt, parseMiniappIcon, parseMiniappIconRev} from '@papan/types'
import { useTenant } from '../hooks/useTenant'

// SHEET MOTION — 300ms on cubic-bezier(0.32,0.72,0,1), shared by every phone
// sheet in the app so they read as one system.
//
// Was 220ms on cubic-bezier(0.2,0.9,0.3,1), which ANIMATED and read as
// instant: measured off the live page, that curve covered 89% of the travel
// in the first 90ms and then spent 130ms imperceptibly settling, so the sheet
// appeared to snap into place. The fix is the CURVE more than the duration —
// this one keeps the motion visible through the middle of its travel, which is
// the part a slide is actually made of.
//
// The exit timeout below must equal this, or the sheet either unmounts
// mid-slide or hangs on screen after it.
const SHEET_MS = 300

/** Shared type glyph — miniapp gradient tile · project purple glyph · else the
 *  normal FileIcon. Reused by the row so the identity matches the sheet. */
export function MobileTypeIcon({ file, size = 22 }: { file: FileItem; size?: number }) {
  if (presentsAsMiniapp(file.name, file.fileType, file.isFolder)) {
    return <MiniappIcon spec={parseMiniappIcon(file.metadataJson)} name={file.name} size={size} refId={file.id} rev={parseMiniappIconRev(file.metadataJson)} className="shrink-0" />
  }
  if (isProjectFolder(file.name)) return <LayoutGrid size={size} className="text-purple-400 shrink-0" />
  return <FileIcon {...fileIconProps(file)} size={size} />
}

function displayName(file: FileItem): string {
  if (presentsAsMiniapp(file.name, file.fileType, file.isFolder)) return stripMiniappFolderExt(file.name)
  if (isProjectFolder(file.name)) return stripProjectFolderExt(file.name)
  return file.name
}

function formatSize(bytes: number): string {
  if (bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

interface Handlers {
  // Tapping the row already opens (folder→browse, file→viewer, .project→
  // workspace), so the sheet carries only SECONDARY + type-specific verbs.
  onBrowse: (f: FileItem) => void       // "Browse as folder" — packages
  onEditApp: (f: FileItem) => void      // .miniapp → open its workspace (workshop)
  onOpenAsApp: (f: FileItem) => void    // loose .html → run it as a chromeless app
  onExtractHere: (f: FileItem) => void  // .zip
  onExtractTo: (f: FileItem) => void    // .zip → destination picker
  onOpenNewTab: (f: FileItem) => void
  onShareLink: (f: FileItem) => void
  onShareMiniApp: (f: FileItem) => void  // HTML-in-.miniapp → share the containing app
  onShareOrg: (f: FileItem) => void
  /** Share internally — grant a role / team / member of this org. Offered on
   *  the SAME rule as the desktop drive's menus (`rowCanManageAccess`): an org
   *  drive, the row's own permission is Manage or owner, and not a dead
   *  `.shared`. A row with no inlined permission — one rebuilt from a realtime
   *  event, e.g. right after a rename, which replaces the row — is judged on
   *  the CURRENT folder's permission (`parentPermission`), the desktop's
   *  folder-level fallback without its role half (a role says nothing about a
   *  row under a folder the person can't see). Neither known → not offered.
   *  At the drive ROOT the listing's folder permission is a fixed `readwrite`,
   *  not a resolved one, so a rebuilt root row stays hidden until the next
   *  listing — narrower than the desktop (which lets the role through), never
   *  wider. */
  onShareInternally?: (f: FileItem) => void
  onWhoHasAccess: (f: FileItem) => void
  onRename: (f: FileItem) => void
  // Touch-native clipboard: Copy/Move → a destination picker in one shot (no
  // Paste step), per the Fable action-model spec.
  onCopyTo: (f: FileItem) => void
  onMoveTo: (f: FileItem) => void
  onDownload: (f: FileItem) => void
  onDelete: (f: FileItem) => void
  /** #218 — only on iOS Safari outside an installed app (`canOfferAddToHomeScreen`). */
  onAddToHomeScreen?: (f: FileItem) => void
}

export function MobileFileSheet({
  file, isOrg, canWrite = true, onClose, miniappAncestors, parentPermission, currentRow, ...h
}: {
  file: FileItem | null; isOrg: boolean; canWrite?: boolean; onClose: () => void; miniappAncestors?: Array<{ id: string; name: string }>
  /** The current folder's resolved permission (`useFiles().parentPermission`) —
   *  the fallback for a row that carries none (see `onShareInternally`). */
  parentPermission?: string
  /** The same row as the listing holds it NOW. `file` is the object captured
   *  when the sheet opened and stays so (a new object would replay the
   *  open — the effect below is keyed on it); an access change re-lists the
   *  folder, and the Share internally gate reads the permission from here so
   *  an OPEN sheet stops offering it when access is lowered. Limits: a refresh
   *  reloads the first page only, so a row reached by "Load more" is not in it
   *  (null → the opened row, as before — the list drops that row too); and a
   *  row rebuilt by a realtime rename carries no permission, so it follows the
   *  folder's, as a reopened sheet and the desktop menu already do. */
  currentRow?: FileItem | null
} & Handlers) {
  const { t } = useTranslation('files')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  // Lets the icon editor offer "pick from Papan Drive" on mobile too.
  const { tenantId } = useTenant()

  // Keep the last file mounted through the slide-DOWN so the exit animates.
  const [mounted, setMounted] = useState<FileItem | null>(file)
  const [shown, setShown] = useState(false)
  /**
   * Has the entry animation FINISHED?
   *
   * `.sheet-rise` is applied while `entering`, and `entering` was
   * `shown && dragY === 0` — which is true again the moment a below-threshold
   * drag springs back to 0. Re-adding an animation class RESTARTS the
   * keyframes, so releasing a 40px drag did not ease back to rest: the sheet
   * snapped to translateY(100%) and slid up again from the bottom edge, with
   * the scrim flashing from transparent to full.
   *
   * Latching on `animationend` means the class comes off once the entry has
   * played and never returns for the life of this sheet, so the drag
   * spring-back is left to the transition that is meant to handle it. Removing
   * the class cannot make it jump: the fill is `backwards`, so once the
   * animation ends the transform already comes from the inline style, which is
   * the same translateY(0).
   */
  const [entered, setEntered] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  // Icon editor is rendered by THIS sheet (not raised to the parent) but must
  // survive the sheet's slide-down: `editIcon` closes the sheet (opensModal) and
  // sets this — kept outside the `mounted` gate so it stays up after unmount.
  const [iconEditorFile, setIconEditorFile] = useState<FileItem | null>(null)
  // The live row (see `currentRow`), kept through the slide-DOWN: on close the
  // screen stops passing it (`file` → null) while `mounted` stays up for
  // SHEET_MS, and falling back to the opened row then would bring Share
  // internally back — tappable — mid-outro. Set only while open.
  const liveRef = useRef<FileItem | null>(null)
  if (file) liveRef.current = currentRow && currentRow.id === file.id ? currentRow : null
  // Swipe-to-dismiss: live drag offset (px) + whether a drag is active.
  const [dragY, setDragY] = useState(0)
  const dragging = useRef(false)
  const startY = useRef(0)
  const dragYRef = useRef(0)
  // When an action opens ANOTHER dialog (Share/Move/…), unmount instantly so the
  // dialog appears in place instead of after a slide-down (the dialog portals to
  // body below this sheet, so we can't keep it visible behind it).
  const instant = useRef(false)

  useEffect(() => {
    if (file) {
      instant.current = false
      setMounted(file); setShowInfo(false); setDragY(0); dragYRef.current = 0
      // Reset `shown` HERE rather than relying on the close having done it.
      //
      // The slide-up only plays if the sheet paints OFF-SCREEN first, which
      // needs `shown === false` at this moment. Every close does set it false,
      // so the open path worked by depending on the close path — and anything
      // that leaves it true costs the animation silently and only on the
      // SECOND open, which is a horrible bug to find: the first open after a
      // reload is always correct.
      //
      // Three ways it can be true here. The parent swaps `file` from one row
      // straight to another with no null in between; a close is interrupted
      // before its state lands; or — the one that actually bites in dev —
      // Fast Refresh re-runs this module while PRESERVING component state, so
      // an edit to this file mid-session can leave `shown` stuck open.
      //
      // Setting it false and back to true across the two frames below is what
      // the double rAF is already for, so this costs nothing and makes the
      // entry animation independent of whatever came before it.
      setEntered(false)
      // Latch `entered` on a TIMER, not on `animationend`.
      //
      // `animationend` only fires if the animation runs to completion, and
      // there are two ways it does not. A drag that starts mid-flight removes
      // `.sheet-rise` (because `entering` requires `dragY === 0`), so the event
      // never arrives and `entered` stays false — then the spring-back re-adds
      // the class and REPLAYS the entry, which is the exact bug this latch was
      // added to prevent, just inside a 300ms window instead of always. And
      // under `prefers-reduced-motion` the animation is `none`, so it never
      // fires at all and the class would stay applied for the sheet's whole
      // life.
      //
      // A timer fires regardless of whether anything animated, which is the
      // property needed here: after SHEET_MS the entry is over by definition.
      const enteredTimer = setTimeout(() => setEntered(true), SHEET_MS)
      setShown(false)
      // Double rAF: paint the off-screen (translate-y-full) frame BEFORE flipping
      // `shown`, so the slide-UP transition actually plays (single rAF sometimes
      // lands in the same paint and skips the animation).
      let r2 = 0
      const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setShown(true)) })
      return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); clearTimeout(enteredTimer) }
    }
    if (instant.current) { setShown(false); setMounted(null); return }
    setShown(false)
    const id = setTimeout(() => setMounted(null), SHEET_MS)
    return () => clearTimeout(id)
  }, [file])

  useEffect(() => {
    if (!file) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [file, onClose])

  // The icon editor lives outside the sheet's mount lifecycle so it stays open
  // after the sheet slides away (editIcon is an opensModal action → onClose).
  const iconDialog = iconEditorFile
    ? createPortal(
        <MiniappIconEditorDialog file={iconEditorFile} tenantId={tenantId} onClose={() => setIconEditorFile(null)} />,
        document.body,
      )
    : null

  if (!mounted) return iconDialog
  const f = mounted
  // The listing's row as it is now, when it is still this one (see `currentRow`).
  const live = liveRef.current && liveRef.current.id === f.id ? liveRef.current : f
  const isMiniapp = presentsAsMiniapp(f.name, f.fileType, f.isFolder)
  const isProject = isProjectFolder(f.name)
  const info = f.isFolder && !isMiniapp && !isProject
    ? ''
    : [formatSize(f.size), new Date(f.updatedAt).toLocaleDateString()].filter(Boolean).join(' · ')
  // The action catalog is SHARED with the desktop menu (buildFileActions) — this
  // sheet only wires the handlers it supports and lays them out.
  const handlerMap: FileActionHandlers = {
    browse: h.onBrowse, editApp: h.onEditApp, openAsApp: h.onOpenAsApp, extractHere: h.onExtractHere, extractTo: h.onExtractTo,
    openNewTab: h.onOpenNewTab, shareLink: h.onShareLink, shareMiniApp: h.onShareMiniApp, shareOrg: h.onShareOrg, whoHasAccess: h.onWhoHasAccess,
    // The catalog offers Share internally whenever a handler is given, so the
    // gate lives here, as on desktop (see the Handlers doc).
    shareInternally: isOrg && !live.shareBroken && canManageAccessFor(live.effectivePermission ?? parentPermission) ? h.onShareInternally : undefined,
    rename: h.onRename, copyTo: h.onCopyTo, moveTo: h.onMoveTo, download: h.onDownload, delete: h.onDelete,
    addToHomeScreen: h.onAddToHomeScreen,
    details: () => setShowInfo((v) => !v),
    editIcon: () => setIconEditorFile(f),
  }
  const actions = buildFileActions(f, { t, isOrg, canWrite, handlers: handlerMap, miniappAncestors })
  const byId = Object.fromEntries(actions.map((a) => [a.id, a])) as Partial<Record<FileActionId, FileActionItem>>
  // Quick strip: Share · Delete · Move · Details. Details (harmless) sits LAST,
  // in the right-thumb rest zone; Delete is pulled inward so it's not the
  // easiest accidental tap. Backfill with Download when read-only (no Delete/
  // Move) so the strip never drops below 3 tiles.
  const strip = [byId.shareLink, byId.delete, byId.moveTo, byId.details].filter(Boolean) as FileActionItem[]
  if (strip.length < 3 && byId.download) strip.splice(1, 0, byId.download)
  const stripIds = new Set(strip.map((a) => a.id))
  const rest = actions.filter((a) => !stripIds.has(a.id))
  // Dismiss policy: inline toggle stays; modal-openers close INSTANTLY (their
  // dialog portals below this sheet); everything else closes + acts.
  const clickFor = (a: FileActionItem) =>
    a.inline ? a.run
    : a.opensModal ? () => { instant.current = true; onClose(); a.run() }
    : () => { onClose(); a.run() }
  const GROUP_ORDER: FileActionItem['group'][] = ['type', 'organize', 'share', 'meta', 'danger']

  // Drag handlers (grabber + header). Pointer events cover touch + mouse.
  const onPointerDown = (e: React.PointerEvent) => {
    // The sheet is not draggable until it has finished arriving.
    //
    // This started as `setEntered(true)` here — latching the entry closed the
    // moment a finger landed, so a drag begun mid-flight could not replay the
    // slide on release. It fixed the replay and bought a smaller glitch: the
    // fill is `backwards`, so removing `.sheet-rise` mid-animation reverts the
    // transform to its resting value at once, and `dragging` has already
    // suppressed the transition — the sheet TELEPORTED the last third of its
    // travel instead of continuing to slide.
    //
    // Refusing the gesture for those 300ms removes both. There is no drag to
    // replay from and nothing to teleport, the timer is then the only thing
    // that needs to latch `entered`, and one invariant is back to having one
    // mechanism. It is also what a platform sheet does: gestures do not take
    // during the presentation animation.
    if (!entered) return
    dragging.current = true; startY.current = e.clientY; dragYRef.current = 0
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return
    const dy = Math.max(0, e.clientY - startY.current)
    dragYRef.current = dy; setDragY(dy)
  }
  const onPointerUp = () => {
    if (!dragging.current) return
    dragging.current = false
    if (dragYRef.current > 90) {
      // Finish the gesture: slide the rest of the way off-screen (transition is
      // re-enabled now that dragging stopped), THEN unmount — so it animates out
      // from the drag position instead of snapping away / vanishing.
      dragYRef.current = 0
      setDragY(window.innerHeight)
      window.setTimeout(onClose, SHEET_MS)
    } else {
      setDragY(0); dragYRef.current = 0
    }
  }

  /**
   * ENTRY is a CSS ANIMATION; EXIT and DRAG stay transitions.
   *
   * `shown` still drives the exit — the parent clears `file`, `shown` goes
   * false, and the sheet transitions back down before unmounting. What it no
   * longer drives is the way IN, because that never reliably worked: a
   * transition from `translateY(100%)` needs the browser to have PAINTED that
   * frame first, and the rAF pair arranging it was scheduled inside a passive
   * effect, before React had re-rendered with the sheet mounted. Win the race
   * and it slides; lose it and the sheet mounts at rest with nothing to
   * animate. Losing got likelier the busier the page, which is why it showed
   * up on the SECOND open and never on the first.
   *
   * `.sheet-rise` cannot lose that race: a keyframe animation starts when the
   * element enters the DOM. It also finishes by itself, so a later re-render
   * does not replay it and the inline transform below takes over cleanly for
   * a drag.
   */
  const entering = shown && dragY === 0 && !entered
  const sheetTransform = dragY > 0 ? `translateY(${dragY}px)` : (shown ? 'translateY(0)' : 'translateY(100%)')
  const backdropOpacity = shown ? Math.max(0, 1 - dragY / 400) : 0

  // Horizontal quick-action tile.
  const Quick = ({ a }: { a: FileActionItem }) => (
    <button onClick={clickFor(a)} className="flex flex-1 flex-col items-center gap-1.5 py-1">
      <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${a.danger ? 'bg-red-500/12 text-red-500' : 'themed-bg-tertiary themed-text'} active:opacity-70`}>
        <a.icon size={20} className={a.iconClassName} />
      </span>
      <span className={`text-[11px] ${a.danger ? 'text-red-500' : 'themed-text-secondary'}`}>{a.label}</span>
    </button>
  )
  // Borderless list row.
  const Row = ({ a }: { a: FileActionItem }) => (
    <button onClick={clickFor(a)} className={`flex w-full items-center gap-3.5 rounded-xl px-3 py-3 text-left text-[15px] active:themed-bg-tertiary ${a.danger ? 'text-red-500' : 'themed-text'}`}>
      <a.icon size={19} className={`shrink-0 ${a.iconClassName ?? ''}`} />
      {a.label}
    </button>
  )

  return (
    <>
      {iconDialog}
      {createPortal(
    <div className={`${themeClass} ws-clear fixed inset-0 z-[200] flex flex-col justify-end`}>
      <div
        className={`sheet-motion absolute inset-0 bg-black/60 ${entering ? 'sheet-scrim-in' : ''}`}
        style={{ opacity: backdropOpacity, transition: dragging.current ? 'none' : `opacity ${SHEET_MS}ms ease-out` }}
        onClick={onClose}
      />
      <div
        className={`sheet-motion relative themed-bg-secondary rounded-t-2xl pb-[max(env(safe-area-inset-bottom),0.75rem)] shadow-2xl ${
          entering ? 'sheet-rise' : ''
        }`}
        style={{ transform: sheetTransform, transition: dragging.current ? 'none' : `transform ${SHEET_MS}ms cubic-bezier(0.32,0.72,0,1)` }}
      >
        {/* grabber + header double as the drag surface (real swipe-to-dismiss) */}
        <div
          className="cursor-grab touch-none active:cursor-grabbing"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div className="flex justify-center pt-2.5 pb-1">
            <span className="h-1.5 w-10 rounded-full themed-bg-tertiary" />
          </div>
          <div className="flex items-center gap-3 px-5 pt-2 pb-3">
            <MobileTypeIcon file={f} size={34} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-base font-medium themed-text">{displayName(f)}</span>
                {isMiniapp && <span className="text-[9px] font-semibold tracking-wide px-1 py-px rounded file-badge-app shrink-0">APP</span>}
              </div>
              {info && <div className="mt-0.5 text-xs themed-text-muted">{info}</div>}
            </div>
          </div>
        </div>

        {/* Quick-action strip (from the shared catalog's `primary` items). */}
        <div className="flex items-stretch gap-1 px-3 pb-1">
          {strip.map((a) => <Quick key={a.id} a={a} />)}
        </div>

        {/* The rest — borderless list, grouped by spacing (type → organize →
            share/access → meta → danger). No dividers. */}
        <div className="max-h-[46vh] overflow-y-auto px-2 pt-1">
          {GROUP_ORDER.map((g) => {
            const items = rest.filter((a) => a.group === g)
            if (!items.length) return null
            return (
              <div key={g}>
                {g !== 'type' && <div className="pt-1" />}
                {items.map((a) => <Row key={a.id} a={a} />)}
              </div>
            )
          })}

          {/* Details expander — toggled by the Details quick-action tile. */}
          {showInfo && (
            <div className="mx-2 mt-1 rounded-xl themed-bg-tertiary px-3 py-2.5 text-xs themed-text-secondary space-y-1">
              <div><span className="themed-text-muted">{t('driveView.header.name')}: </span>{f.name}</div>
              {!f.isFolder && <div><span className="themed-text-muted">{t('driveView.header.size')}: </span>{formatSize(f.size) || '—'}</div>}
              <div><span className="themed-text-muted">{t('driveView.header.dateModified')}: </span>{new Date(f.updatedAt).toLocaleString()}</div>
              {isOrg && f.creator?.name && <div><span className="themed-text-muted">{t('driveView.header.createdBy')}: </span>{f.creator.name}</div>}
              {isOrg && (f.floor === 'admin' || f.floor === 'member' || (f.accessSummary?.links ?? 0) > 0) && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5">
                  {f.floor === 'admin' && <span className="inline-flex items-center gap-1"><Lock size={11} /> {t('driveView.access.limited')}</span>}
                  {f.floor === 'member' && <span>{t('driveView.access.memberPlus')}</span>}
                  {(f.accessSummary?.links ?? 0) > 0 && <span className="inline-flex items-center gap-1"><Link2 size={11} /> {f.accessSummary!.links}</span>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
      )}
    </>
  )
}
